import { Calendar, Check, Plus, MapPin, Edit, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PageSkeleton } from '@/components/ui/page-skeleton'
import { Badge } from '@/components/ui/badge'

export interface RoadmapStep {
  id: number
  titre: string
  description: string | null
  date: string
  visibilite_client?: boolean
}

interface RoadmapSectionProps {
  steps: RoadmapStep[]
  onNewStep: () => void
  onEditStep?: (step: RoadmapStep) => void
  onDeleteStep?: (stepId: number, stepTitle: string) => void
  sortOrder: 'oldest' | 'newest'
  onSortOrderChange: (order: 'oldest' | 'newest') => void
  loading?: boolean
  isAdmin?: boolean
}

export function RoadmapSection({ steps, onNewStep, onEditStep, onDeleteStep, sortOrder, onSortOrderChange, loading = false, isAdmin = true }: RoadmapSectionProps) {
  // Format date to DD/MM/YYYY
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  // Sort steps by date based on sortOrder
  const sortedSteps = [...steps].sort((a, b) => {
    const dateA = new Date(a.date).getTime()
    const dateB = new Date(b.date).getTime()
    return sortOrder === 'oldest' ? dateA - dateB : dateB - dateA
  })

  // Étapes passées / prochaine étape, calculées à partir des dates existantes
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  const isPast = (step: RoadmapStep) => new Date(step.date).getTime() < startOfToday.getTime()
  const nextStepId = [...steps]
    .filter((step) => !isPast(step))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0]?.id

  return (
    <section aria-labelledby="roadmap-title" className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-brand-violet">
            <MapPin className="w-[18px] h-[18px] text-white" />
          </span>
          <div>
            <h2 id="roadmap-title" className="text-[22px] font-semibold text-brand-navy">Roadmap Projet</h2>
            <p className="text-sm text-gray-500">Étapes clés et jalons du projet</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <label className="flex items-center gap-1.5 text-[13px] text-gray-600">
            Tri
            <select
              value={sortOrder}
              onChange={(e) => onSortOrderChange(e.target.value as 'oldest' | 'newest')}
              className="h-[38px] rounded-[10px] border border-gray-300 bg-white px-2.5 text-[13px] text-brand-navy focus:border-brand focus:ring-brand"
            >
              <option value="oldest">Plus anciennes</option>
              <option value="newest">Plus récentes</option>
            </select>
          </label>
          {isAdmin && (
            <Button
              onClick={onNewStep}
              className="h-[38px] rounded-[10px] bg-brand text-white hover:bg-brand-dark"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une étape
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-[18px] border border-gray-200 bg-white p-6">
        {loading ? (
          <PageSkeleton variant="rows" />
        ) : steps.length === 0 ? (
          <div className="text-center py-6 text-gray-500">Aucune étape de roadmap disponible</div>
        ) : (
          <ol className="flex flex-col">
            {sortedSteps.map((step, index) => {
              const past = isPast(step)
              const isNext = step.id === nextStepId
              const isLast = index === sortedSteps.length - 1
              return (
                <li key={step.id} className="flex gap-4">
                  {/* Point et ligne de la frise */}
                  <div className="flex flex-col items-center">
                    {past ? (
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand-violet">
                        <Check className="h-3 w-3 text-white" strokeWidth={3.5} />
                      </span>
                    ) : (
                      <span
                        className={`h-6 w-6 flex-shrink-0 rounded-full border-[3px] bg-white ${isNext ? 'border-brand-violet' : 'border-gray-300'}`}
                      />
                    )}
                    {!isLast && <span className={`w-0.5 flex-1 ${past ? 'bg-brand-violet' : 'bg-[#D9D3F5]'}`} />}
                  </div>

                  <div
                    className={`mb-5 flex flex-1 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between ${isNext ? 'rounded-xl bg-brand-violet-light px-4 py-3.5' : 'pb-1'}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className={`text-[15px] font-semibold ${!past && !isNext ? 'text-gray-600' : 'text-brand-navy'}`}>
                          {step.titre}
                        </h3>
                        {isNext && (
                          <span className="rounded-full bg-brand-violet px-2 py-0.5 text-[11px] font-semibold text-white">
                            Prochaine étape
                          </span>
                        )}
                        {isAdmin && step.visibilite_client && (
                          <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">
                            Visible client
                          </Badge>
                        )}
                        {isAdmin && step.visibilite_client === false && (
                          <Badge variant="secondary" className="bg-gray-100 text-gray-600 border-gray-200">
                            Masqué client
                          </Badge>
                        )}
                      </div>
                      {step.description && (
                        <p className="mt-0.5 text-sm text-gray-600 break-words whitespace-normal">{step.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`flex items-center gap-1.5 whitespace-nowrap text-[13px] ${isNext ? 'font-semibold text-brand-violet-dark' : 'text-gray-500'}`}>
                        <Calendar className="w-4 h-4" />
                        {formatDate(step.date)}
                      </span>
                      {isAdmin && (
                        <div className="flex items-center gap-1">
                          {onEditStep && (
                            <button
                              onClick={() => onEditStep(step)}
                              className="p-2 text-gray-400 hover:text-brand hover:bg-brand-light rounded-lg transition-colors"
                              title="Modifier l'étape"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          )}
                          {onDeleteStep && (
                            <button
                              onClick={() => onDeleteStep(step.id, step.titre)}
                              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Supprimer l'étape"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ol>
        )}
      </div>
    </section>
  )
}
